// Heading-level font sizing — the single source of truth for all module
// and page-content font size controls. Replaces the old per-control
// pixel sliders with a 6-step semantic scale that resolves to the
// Site Styles CSS variables (--h1-size … --h6-size). When admins tweak
// Site Styles, every module that uses these levels updates instantly.

export const HEADING_LEVELS = [
  { value: "h1", label: "H1 — Display" },
  { value: "h2", label: "H2 — Page title" },
  { value: "h3", label: "H3 — Section title" },
  { value: "h4", label: "H4 — Subsection" },
  { value: "h5", label: "H5 — Small heading" },
  { value: "h6", label: "H6 — Caption" },
];

export const DEFAULT_HEADING_LEVEL = "h3";

// Fallback px sizes when no Site Styles variable is loaded (e.g. while
// the public CSS variables haven't been injected yet or in isolated
// previews/iframes). These match the defaults in useSiteStyles.js.
const FALLBACK_PX = {
  h1: 48,
  h2: 36,
  h3: 24,
  h4: 20,
  h5: 18,
  h6: 16,
};

/**
 * Returns an inline `fontSize` value usable in React `style={{...}}`.
 * Backwards-compatible with legacy numeric (px) module configs:
 *   - number / numeric string  → `${value}px`
 *   - "h1" … "h6"              → `var(--hN-size, fallbackPx)`
 *   - anything else / blank    → resolves to the fallback level (H3)
 */
export function resolveFontSize(value, fallbackLevel = DEFAULT_HEADING_LEVEL) {
  if (value === null || value === undefined || value === "") {
    const lvl = (fallbackLevel || DEFAULT_HEADING_LEVEL).toLowerCase();
    return `var(--${lvl}-size, ${FALLBACK_PX[lvl] || 16}px)`;
  }
  if (typeof value === "number") return `${value}px`;
  if (typeof value === "string") {
    if (/^\d+(\.\d+)?$/.test(value.trim())) return `${parseFloat(value)}px`;
    if (/^h[1-6]$/i.test(value)) {
      const lvl = value.toLowerCase();
      return `var(--${lvl}-size, ${FALLBACK_PX[lvl]}px)`;
    }
  }
  const lvl = (fallbackLevel || DEFAULT_HEADING_LEVEL).toLowerCase();
  return `var(--${lvl}-size, ${FALLBACK_PX[lvl] || 16}px)`;
}

// Normalises any legacy or invalid value back to one of "h1"…"h6" so
// the admin UI <select> stays in a controlled, predictable state.
export function normaliseHeadingLevel(value) {
  if (typeof value === "string" && /^h[1-6]$/i.test(value)) return value.toLowerCase();
  return DEFAULT_HEADING_LEVEL;
}

/**
 * Returns a numeric pixel value for a level — used where CSS variables
 * aren't accepted (e.g. SVG icon sizes, JS measurements). Falls back to
 * the FALLBACK_PX table; legacy numeric values pass through unchanged.
 */
export function resolveFontSizePx(value, fallbackLevel = DEFAULT_HEADING_LEVEL) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    if (/^\d+(\.\d+)?$/.test(value.trim())) return parseFloat(value);
    if (/^h[1-6]$/i.test(value)) return FALLBACK_PX[value.toLowerCase()];
  }
  return FALLBACK_PX[(fallbackLevel || DEFAULT_HEADING_LEVEL).toLowerCase()] || 16;
}
